import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { buildLanguageContinuationPath } from '../utils/languageRouting';
import ExamSettingsFields from './ExamSettingsFields';
import { isValidExamDate } from '../utils/examDate';

const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'French', flag: '/flags/fr.svg' },
  { code: 'de', label: 'German', flag: '/flags/de.svg' },
];

export default function RequiredLanguageSelectionDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { changeLanguage, shouldRequireLanguageSelection, languageChangePending, pendingLanguage } = useLanguage();
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [targetExamLevel, setTargetExamLevel] = useState('');
  const [examDate, setExamDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const open = useMemo(() => Boolean(user && shouldRequireLanguageSelection), [user, shouldRequireLanguageSelection]);
  const saving = submitting || languageChangePending;

  useEffect(() => {
    setSelectedLanguage('');
    setTargetExamLevel('');
    setExamDate('');
    setErrorMessage('');
  }, [user?.uid, open]);

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
  };

  const handleContinue = async () => {
    if (saving || !selectedLanguage || !isValidExamDate(examDate)) {
      return;
    }

    setErrorMessage('');

    setSubmitting(true);
    try {
      const examSettings = {
        ...(targetExamLevel ? { targetExamLevel } : {}),
        ...(examDate ? { examDate } : {}),
      };
      const result = await changeLanguage(selectedLanguage, {
        suppressFailureToast: true,
        ...(Object.keys(examSettings).length ? { examSettings } : {}),
      });

      if (!result.success) {
        setErrorMessage(result.message || 'We could not save your choices.');
        return;
      }

      const continuationPath = buildLanguageContinuationPath(location, result.language ?? selectedLanguage);
      if (continuationPath && continuationPath !== `${location.pathname}${location.search}${location.hash}`) {
        navigate(continuationPath, { replace: true });
      }
    } catch (_) {
      setErrorMessage('We could not save your choices. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={handleClose}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          px: { xs: 0.5, sm: 1 },
          py: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, textAlign: 'center', fontWeight: 900, color: 'primary.main' }}>
        Choose your practice language
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          Select French or German to continue.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
          {LANGUAGE_OPTIONS.map(({ code, label, flag }) => {
            const isActive = selectedLanguage === code;
            const isSaving = saving && (pendingLanguage ?? selectedLanguage) === code;

            return (
              <Button
                key={code}
                variant={isActive ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() => setSelectedLanguage(code)}
                disabled={saving}
                aria-pressed={isActive}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 64,
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  borderRadius: 3,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 32,
                    height: 22,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={flag}
                    alt={`${label} flag`}
                    style={{
                      width: '32px',
                      height: 'auto',
                      borderRadius: '2px',
                      objectFit: 'cover',
                      opacity: isSaving ? 0.35 : 1,
                    }}
                  />
                  {isSaving && <CircularProgress size={22} color="inherit" sx={{ position: 'absolute' }} />}
                </Box>
                <Box component="span">{label}</Box>
              </Button>
            );
          })}
        </Stack>
        <ExamSettingsFields
          targetExamLevel={targetExamLevel}
          onTargetExamLevelChange={setTargetExamLevel}
          examDate={examDate}
          onExamDateChange={setExamDate}
          disabled={saving}
        />
        <Button
          variant="contained"
          fullWidth
          onClick={handleContinue}
          disabled={!selectedLanguage || saving || !isValidExamDate(examDate)}
        >
          Continue
        </Button>
      </DialogContent>
    </Dialog>
  );
}
